import type { ReactElement, ReactNode } from 'react';
import React, { useCallback } from 'react';
import uniq from 'lodash/uniq';
import Table from 'atom-components/table-outdated/table';
import { useURLSearchParams } from 'hooks/useURLSearchParams';
import { useIsSchool } from 'services/providers/auth.hooks';
import type { ColumnHideOptions, MinUser } from './table-definitions';
import { useTableProps } from './table-definitions';

export default function UsersTable<User extends MinUser>({
  users,
  actionRender,
  onSelectRow,
  ...hideOptions
}: {
  users: readonly User[];
  onSelectRow?(ids: string[]): void;
  actionRender?(row: User): ReactNode;
} & ColumnHideOptions): ReactElement {
  const isSchool = useIsSchool();
  // set some defaults for the hide options
  const {
    hideNameColumn = !users.filter(({ firstName }) => firstName)?.length,
    hideLevelColumn = !isSchool,
    hideGenderColumn = !users.filter(({ genderId }) => genderId)?.length,
    hideClassColumn = !users.filter(({ groupIds }) => groupIds?.length)?.length,
    hideEthnicityColumn = !users.filter(({ ethnicityIds }) => ethnicityIds?.length)?.length,
    ...restHideOptions
  } = hideOptions;
  const statusOptions = hideOptions.isAssessmentUser
    ? uniq(users.map((u) => u.response?.status))
    : [];
  const searchParams = useURLSearchParams();

  const getUserLink = useCallback(
    (id: number) => {
      searchParams.set('type', 'individual');
      searchParams.set('assessmentUserId', `${id}`);
      return `../individual?${searchParams.toString()}`;
    },
    [searchParams]
  );

  const tableProps = useTableProps({
    actionRender,
    statusOptions,
    hideNameColumn,
    hideLevelColumn,
    hideClassColumn,
    hideGenderColumn,
    hideEthnicityColumn,
    getUserLink,
    ...restHideOptions
  });

  return (
    <Table
      {...tableProps}
      data={users}
      onSelectRow={onSelectRow}
      getRowId={(row): string => row.id}
      pageSize={15}
    />
  );
}
